// Copyright 2025, University of Maryland, All Rights Reserved

/**
 * Shared dataset fetching service.
 * Simple API calls for fetching analysis datasets.
 */

import type { NodeApiClient } from '@/app-shell/services'

/** Response from dataset endpoints (full payload from S3) */
export interface DatasetResponse<T = Record<string, unknown>> {
  payload: T
  updatedAt?: string | null
  documentId?: string
  agentId?: string
  datasetKey?: string
  /** Which run this payload came from */
  run?: number
}

/** LLM execution metrics for one analysis, as stored in the S3 payload. */
export interface LlmMetrics {
  modelVariant: string | null
  promptTokens: number
  completionTokens: number
  cacheReadTokens: number
  cacheWriteTokens: number
  /** Provenance only — the costs subsystem is the system of record for spend. */
  cost: number | null
}

/**
 * Read execution metrics out of a dataset payload. The analysis node writes them
 * alongside the report so they travel with the artifact they describe.
 * Returns null for payloads written before metrics were stored there.
 */
export function extractLlmMetrics(res: DatasetResponse | null): LlmMetrics | null {
  const metrics = (res?.payload as { llmMetrics?: Record<string, unknown> } | undefined)?.llmMetrics
  if (!metrics) return null
  const count = (value: unknown): number => (typeof value === 'number' ? value : 0)
  return {
    modelVariant: typeof metrics.modelVariant === 'string' ? metrics.modelVariant : null,
    promptTokens: count(metrics.promptTokens),
    completionTokens: count(metrics.completionTokens),
    cacheReadTokens: count(metrics.cacheReadTokens),
    cacheWriteTokens: count(metrics.cacheWriteTokens),
    cost: typeof metrics.cost === 'number' ? metrics.cost : null,
  }
}

/**
 * Which run of a dataset to read. Every run against a document version is kept,
 * so a fetch that names none is asking for the most recent.
 */
export interface DatasetRunSelector {
  /** Specific run. Omit for the latest. */
  run?: number
  /**
   * For diff-mode datasets, the version this run compared against. Several runs
   * can sit at one document version having compared against different
   * predecessors, and only those sharing a basis answer the same question.
   */
  comparedTo?: number
}

/**
 * Fetch one agent's dataset payload.
 *
 * Dataset keys are only unique within their owning agent, so the agent is part
 * of the address — not an optional refinement of it.
 */
export async function fetchDatasetByKey<T = Record<string, unknown>>(
  apiClient: NodeApiClient,
  orgId: string,
  wsId: string,
  documentId: string,
  agentId: string,
  datasetKey: string,
  version?: number,
  groupId?: string,
  selector?: DatasetRunSelector,
): Promise<DatasetResponse<T>> {
  const params = new URLSearchParams()
  if (version != null) params.set('version', String(version))
  if (groupId) params.set('group_id', groupId)
  if (selector?.run != null) params.set('run', String(selector.run))
  if (selector?.comparedTo != null) params.set('compared_to', String(selector.comparedTo))
  const query = params.toString()
  const url = `/org/${orgId}/ws/${wsId}/datasets/documents/${documentId}/${agentId}/${datasetKey}${query ? `?${query}` : ''}`
  const res = await apiClient.get<DatasetResponse<T>>(url)
  return res.data
}

/**
 * Fetch several of one agent's datasets in parallel.
 * Returns a map of datasetKey -> response (or null if not found).
 */
export async function fetchMultipleDatasets(
  apiClient: NodeApiClient,
  orgId: string,
  wsId: string,
  documentId: string,
  agentId: string,
  datasetKeys: string[],
  version?: number,
  groupId?: string,
): Promise<Map<string, DatasetResponse | null>> {
  const results = await Promise.all(
    datasetKeys.map(async (key) => {
      try {
        const response = await fetchDatasetByKey(apiClient, orgId, wsId, documentId, agentId, key, version, groupId)
        return [key, response] as const
      } catch {
        return [key, null] as const
      }
    })
  )
  return new Map(results)
}

/**
 * The latest attempt plus the last successful result for one document version.
 * Failed reruns update `d`, `t`, and `e` without replacing `sd`, allowing the UI
 * to explain the failure while keeping the previous completed result available.
 */
export interface AnalysisStatusEntry {
  s?: number | null
  t?: string | null  // 'positive' | 'neutral' | 'negative' | 'error' | 'completed'
  d?: string | null  // ISO timestamp of the latest attempt
  sd?: string | null // ISO timestamp of the last successful attempt
  e?: string | null  // failure message from the latest attempt
  c?: number | null  // findings count for this dataset/version
}

/** Response from the analysis status endpoint — grouped by agent, then dataset */
export interface AnalysisStatusResponse {
  agents: Record<string, Record<string, Record<string, AnalysisStatusEntry>>>
}

/** One agent's dataset. Bare keys are ambiguous — two agents can share one. */
export interface AgentDataset {
  agentId: string
  datasetKey: string
}

const statusUrl = (orgId: string, wsId: string, params: URLSearchParams) =>
  `/org/${orgId}/ws/${wsId}/datasets/analysis-status?${params.toString()}`

/**
 * Fetch every analysis status owned by the given agents.
 * Returns {agents: {agentId: {datasetKey: {docId_vN: {s, t, d}}}}}.
 *
 * The agent is the unit of retrieval: the server holds one prefix per agent, so
 * callers do not enumerate the dataset keys they want.
 */
export async function fetchAnalysisStatuses(
  apiClient: NodeApiClient,
  orgId: string,
  wsId: string,
  agentIds: string[],
  groupId?: string,
): Promise<AnalysisStatusResponse> {
  if (agentIds.length === 0) return { agents: {} }
  const params = new URLSearchParams()
  for (const agentId of agentIds) params.append('agent', agentId)
  if (groupId) params.set('group_id', groupId)
  const res = await apiClient.get<AnalysisStatusResponse>(statusUrl(orgId, wsId, params))
  return res.data
}

/**
 * Fetch one document's statuses for the given agent datasets. Same response
 * shape as `fetchAnalysisStatuses`.
 *
 * A document is the tail of the server's sort key, so it is not addressable on
 * its own — passing the dataset keys turns what would be a scan of every agent's
 * results into one point read per key.
 */
export async function fetchDocumentAnalysisStatuses(
  apiClient: NodeApiClient,
  orgId: string,
  wsId: string,
  datasets: AgentDataset[],
  docId: string,
  groupId?: string,
): Promise<AnalysisStatusResponse> {
  if (datasets.length === 0) return { agents: {} }
  const params = new URLSearchParams()
  for (const agentId of new Set(datasets.map(d => d.agentId))) params.append('agent', agentId)
  for (const datasetKey of new Set(datasets.map(d => d.datasetKey))) params.append('dataset', datasetKey)
  params.set('doc_id', docId)
  if (groupId) params.set('group_id', groupId)
  const res = await apiClient.get<AnalysisStatusResponse>(statusUrl(orgId, wsId, params))
  return res.data
}
